// The denominator (#11426).
//
// What you are NOT being shown, stated under the thing it counts. Above a
// table a count reads as a claim about the page; below it, it reads as what it
// is — a statement about the rest. Callers put this at the bottom.

import React from "react";

export interface DenominatorProps {
  shown: number;
  total: number;
  /** Plural, lowercase — "documents", "interactions". */
  noun: string;
  /**
   * The total is a floor, not an exact count — the source paged out before
   * the end. Renders "5 of 200+ documents": an honest floor rather than a
   * fabricated total, matching the tab headers' "N+" convention.
   */
  openEnded?: boolean;
  /** Appended after the counts, e.g. a "View all" doorway. */
  trailing?: React.ReactNode;
}

/**
 * The sentence a denominator makes, or `null` when it has none to make.
 *
 * One computation owns both the number and its silence (register rule 22).
 * The completeness test used to be re-evaluated at each render site that
 * needed it — once for the text, once for the separator — and a predicate
 * copied is a predicate that drifts. Here the silence is not a second
 * decision about the count; it is the absence of the count itself.
 *
 * A complete set says nothing, because absence is how a reader learns the set
 * is whole, and that only reads as "whole" if it is absent EVERYWHERE the set
 * is whole. An open-ended total is never complete: its "+" is the claim that
 * more exists past what was counted.
 */
export function denominatorStatement({
  shown,
  total,
  noun,
  openEnded,
}: Omit<DenominatorProps, "trailing">): string | null {
  // Silence wins past the total in BOTH directions. Without the first
  // clause an open-ended set rendered "8 of 6+", asserting a floor the
  // numerator already exceeds — the same dishonesty the closed case
  // avoids by saying nothing.
  if (shown > total) return null;
  if (!openEnded && shown >= total) return null;
  return `${shown} of ${total}${openEnded ? "+" : ""} ${noun}`;
}

export const Denominator: React.FC<DenominatorProps> = ({
  shown,
  total,
  noun,
  openEnded,
  trailing,
}) => {
  const statement = denominatorStatement({ shown, total, noun, openEnded });
  // Nothing to say and nowhere to go: render nothing at all, rather than a
  // padded empty row holding space where a complete set used to speak.
  if (!statement && !trailing) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: "var(--fp-gap-tight)",
        paddingTop: "var(--fp-gap-tight)",
        fontFamily: "var(--fp-font-ui)",
        fontFeatureSettings: "var(--fp-feat-num)",
        fontSize: "var(--fp-t-meta)",
        color: "var(--fp-ink-3)",
      }}
    >
      {/* Both reads below ask the one question — "is there a statement?" —
          rather than each re-deciding whether the set is complete. The
          trailing slot survives a silent denominator on its own terms: an
          overflow doorway dies with its overflow, but a navigation
          affordance names its destination and does not depend on a count
          existing (keeper ruling, all-kinds sweep). */}
      {statement ? <span>{statement}</span> : null}
      {trailing ? (
        <>
          {statement ? <span aria-hidden="true">·</span> : null}
          {trailing}
        </>
      ) : null}
    </div>
  );
};
